
import React, { useState } from 'react';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { formatCertificateNumber } from '@/utils/certificateUtils';
import { CertificateNumberInventory } from '@/utils/certificateInventoryTypes';
import { CertificateStatusBadge } from './CertificateStatusBadge';
import { 
  FileCheck, 
  XCircle, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Check
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';

// Update the interface to match what CertificateTrackingDashboard provides
interface DiscrepancyItem extends Omit<CertificateNumberInventory, 'created_at'> {
  discrepancyType: 'unreported' | 'unverified' | 'conflicting';
  discrepancyDetails?: string;
  created_at?: string | null;
}

interface CertificateDiscrepancyListProps {
  discrepancies: DiscrepancyItem[];
  onResolve: (certificate: DiscrepancyItem) => void;
  isLoading?: boolean;
}

export function CertificateDiscrepancyList({
  discrepancies,
  onResolve,
  isLoading = false
}: CertificateDiscrepancyListProps) {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  
  if (discrepancies.length === 0 && !isLoading) {
    return null;
  }
  
  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Certificate Discrepancies</CardTitle>
            <CardDescription>
              Certificates with reporting or verification inconsistencies
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <ScrollArea className="h-[400px] rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate #</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Course Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading discrepancies...
                    </TableCell>
                  </TableRow>
                ) : discrepancies.length > 0 ? (
                  discrepancies.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">
                        {formatCertificateNumber(item.certificate_number)}
                      </TableCell>
                      <TableCell>{item.state}</TableCell>
                      <TableCell>{item.course_type}</TableCell>
                      <TableCell>
                        {item.is_used ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700">Used</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700">Available</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.discrepancyType === 'unreported' && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            Not Reported
                          </Badge>
                        )}
                        {item.discrepancyType === 'unverified' && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 flex gap-1 items-center">
                            <AlertTriangle className="h-3 w-3" />
                            Not Verified
                          </Badge>
                        )}
                        {item.discrepancyType === 'conflicting' && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 flex gap-1 items-center">
                            <AlertTriangle className="h-3 w-3" />
                            Status Conflict
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => onResolve(item)}
                          className="flex gap-1 items-center"
                        >
                          <FileCheck className="h-4 w-4" />
                          Resolve
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <Check className="h-8 w-8 text-green-500" />
                        <p>No discrepancies found. All certificates are properly tracked.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      )}
    </Card>
  );
}
